import React, { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Coins, Package, Truck } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import InviteButton from "@/components/InviteButton";
import ProfileDialog from "@/components/ProfileDialog";
import Logo from "@/components/Logo";

const TIER_COLORS = {
  new: "bg-slate-100 text-slate-700",
  tier1: "bg-blue-100 text-blue-700",
  tier2: "bg-purple-100 text-purple-700",
  tier3: "bg-amber-100 text-amber-800",
};

const TopBar = ({ balance }) => {
  const { user, mode, modeChosen, switchMode } = useAuth();
  const nav = useNavigate();
  const carrier = mode === "carrier";
  useEffect(() => {
    document.body.dataset.mode = mode;
    return () => { delete document.body.dataset.mode; };
  }, [mode]);
  return (
    <header className={`sticky top-0 z-30 backdrop-blur-xl border-b-2 ${carrier ? "bg-teal-50/90 border-teal-600" : "bg-amber-50/90 border-amber-500"}`} data-testid="app-header">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-1.5 sm:gap-3">
        <button onClick={() => nav("/app")} className="flex items-center gap-2" data-testid="header-home-link">
          <Logo size={34} variant="dark"/>
          <span className="font-display font-bold hidden sm:block">CarryIt</span>
        </button>
        {modeChosen && <span data-testid="mode-label" className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white ${carrier ? "bg-teal-700" : "bg-amber-700"}`}>
          {carrier ? <Truck className="w-3.5 h-3.5"/> : <Package className="w-3.5 h-3.5"/>}
          <span className="hidden sm:inline">{carrier ? "Carry mode" : "Send mode"}</span>
          <span className="sm:hidden">{carrier ? "Carry" : "Send"}</span>
        </span>}

        <div className={`flex-1 min-w-0 max-w-sm mx-auto ${modeChosen ? "" : "invisible"}`}>
          <div className="flex bg-white/80 border border-slate-200 rounded-full p-1" data-testid="mode-switcher">
            <button
              data-testid="mode-sender"
              onClick={() => switchMode("sender")}
              className={`flex-1 text-xs sm:text-sm px-2 py-1.5 rounded-full font-medium transition ${mode === "sender" ? "bg-amber-700 shadow text-white" : "text-slate-600"}`}
              aria-pressed={mode === "sender"}
            >Send</button>
            <button
              data-testid="mode-carrier"
              onClick={() => switchMode("carrier")}
              className={`flex-1 text-xs sm:text-sm px-2 py-1.5 rounded-full font-medium transition ${mode === "carrier" ? "bg-teal-700 shadow text-white" : "text-slate-600"}`}
              aria-pressed={mode === "carrier"}
            >Carry</button>
          </div>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
          {typeof balance === "number" && (
            <div className="hidden sm:flex items-center gap-1.5 text-sm bg-white text-slate-800 px-3 py-1.5 rounded-full border border-slate-200" data-testid="wallet-badge" title={carrier ? "Earnings" : "CarryIt balance"}>
              <Coins className="w-3.5 h-3.5"/> {carrier ? "Earnings" : "Balance"} ₹{balance.toFixed(0)}
            </div>
          )}
          <Badge className={`hidden sm:inline-flex ${TIER_COLORS[user?.trust_tier] || TIER_COLORS.new}`}>{(user?.trust_tier || "new").toUpperCase()}</Badge>
          <span className="hidden sm:inline-flex"><InviteButton /></span>
          <NotificationBell />
          <ProfileDialog />
        </div>
      </div>
    </header>
  );
};

export default TopBar;
